import {
  adminDangerGhostBtnClass,
  ghostBtnClass,
  mfgConfirmDialogClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgInventoryCountSession } from "@/lib/manufacturing/types/inventoryCount";

export type InventoryCountOpenDraft = {
  session: MfgInventoryCountSession;
  line_count: number;
  counted_line_count: number;
};

type Props = {
  open: boolean;
  busy: boolean;
  draft: InventoryCountOpenDraft | null;
  onResume: () => void;
  onStartNew: () => void;
  onDiscard: () => void;
  onCancel: () => void;
};

function formatOpenedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/**
 * Shown when Start / resume finds an in-progress plant count.
 * Exit without Submit keeps the session open; this is how the operator
 * resumes, starts a fresh snapshot, or archives (discards) the draft.
 */
export function ManufacturingInventoryCountOpenChooser({
  open,
  busy,
  draft,
  onResume,
  onStartNew,
  onDiscard,
  onCancel,
}: Props) {
  if (!open || !draft) return null;

  const { session, line_count, counted_line_count } = draft;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-count-open-chooser-title"
        className={`max-w-lg w-full ${mfgConfirmDialogClass}`}
      >
        <h3
          id="inventory-count-open-chooser-title"
          className="text-base font-semibold text-gray-900 dark:text-zinc-50"
        >
          In-progress inventory count
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-zinc-300">
          Started {formatOpenedAt(session.opened_at)} · {counted_line_count} of {line_count}{" "}
          materials counted. Exit saves this count until you submit or discard it.
        </p>
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Submit is the only path that posts stock adjustments. Discard archives this session
          without changing on-hand. Start new archives it, then snapshots current on-hand.
        </p>
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button type="button" className={ghostBtnClass} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={adminDangerGhostBtnClass}
            onClick={onDiscard}
            disabled={busy}
          >
            Discard (archive)
          </button>
          <button type="button" className={ghostBtnClass} onClick={onStartNew} disabled={busy}>
            Start new count
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            onClick={onResume}
            disabled={busy}
            autoFocus
          >
            Resume count
          </button>
        </div>
      </div>
    </div>
  );
}
